import React from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon } from '@heroicons/react/24/outline';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { formatEventDate } from '@/dashboard/lib/format-date';
import SelectedItemCard, { SelectedItemSummary } from './SelectedItemCard';
import TicketTypeModalCheckbox from './TicketTypeMultiSelect/TicketTypeModalCheckbox';
import SelectedTicketsDisplay from './SelectedTicketsDisplay';
import TicketTypeMultiSelectList from './TicketTypeMultiSelect/TicketTypeMultiSelectList';

const PageSummary = ({ page }) => (
  <SelectedItemSummary typeLabel={page.type === 'event' ? 'Event' : 'Checkout'} title={page.name}>
    {page.type === 'checkout' && (
      <div className="text-sm text-muted-foreground">{getProductPriceLabel(page.product)}</div>
    )}
    {page.type === 'event' && page.eventDetails?.startDate && (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <CalendarIcon className="size-3.5 shrink-0" />
        {formatEventDate({
          startDate: page.eventDetails.startDate,
          endDate: page.eventDetails.endDate,
          timezone: page.eventDetails.timezone,
        }).join(' - ')}
      </div>
    )}
  </SelectedItemSummary>
);

const PageMultiSelectPageContainer = ({
  page,
  onRemove,
  onVisit,
  showRemoveButton = true,
  includeTicketTypes = false,
  selectedTicketIds = [],
  onTicketTypeChange,
  isReadOnly = false,
  directTicketTypes = null,
  children,
}) => {
  const currency = page.eventDetails?.currency;

  const handleVisitClick = () => {
    if (onVisit) {
      onVisit(page._id);
    }
  };

  const handleRemoveClick = () => {
    if (onRemove) {
      onRemove(page);
    }
  };

  return (
    <SelectedItemCard
      summary={<PageSummary page={page} />}
      name={page.name}
      onVisit={handleVisitClick}
      onRemove={showRemoveButton && !isReadOnly ? handleRemoveClick : undefined}
      footer={children}
    >
      {includeTicketTypes && page.type === 'event' && (
        <>
          {directTicketTypes && directTicketTypes.length > 0 ? (
            <div className="mt-2">
              <TicketTypeMultiSelectList
                ticketTypes={directTicketTypes}
                selectedTicketTypes={new Map()}
                interactive={false}
                currency={currency}
              />
            </div>
          ) : (
            <>
              <SelectedTicketsDisplay page={page} selectedTicketIds={selectedTicketIds} />
              {!isReadOnly && (
                <TicketTypeModalCheckbox
                  page={page}
                  onChange={onTicketTypeChange}
                  initialValue={selectedTicketIds}
                />
              )}
            </>
          )}
        </>
      )}
    </SelectedItemCard>
  );
};

PageMultiSelectPageContainer.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    product: PropTypes.object,
    eventDetails: PropTypes.object,
  }).isRequired,
  onRemove: PropTypes.func,
  onVisit: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  includeTicketTypes: PropTypes.bool,
  selectedTicketIds: PropTypes.arrayOf(PropTypes.string),
  onTicketTypeChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
  directTicketTypes: PropTypes.arrayOf(PropTypes.object),
};

export default PageMultiSelectPageContainer;
